import type { AssistentAntwort, ChatNachricht } from './hilfeApi';
import { hilfeAdresse } from './hilfeLeiste';
import { CHAT_TEXTE, grenzeStunde, zeichenZaehler } from './hilfeTexte';

// Reine Funktionen des Chats mit dem Hilfe-Assistenten (Profil-Nachbau F-06, BAU-HILFE.md 1.4 und 3.3; PF-309), ohne JSX und
// ohne Browser testbar (tests/hilfe-chat.test.mjs). HilfeChat.tsx zeigt nur, was diese Funktionen liefern.

/** Grenzen der Eingabe wie der Server (assistent.ts: text 1..2000). */
export const CHAT_GRENZE = 2000;
export const ZAEHLER_AB = 1800;

// Tausenderpunkt wie im Wortlaut („2.000“); Intl setzt für de-AT ein geschütztes Leerzeichen.
const zahl = (n: number) => n.toLocaleString('de-AT').replace(/[\s  ]/g, '.');

/** „{n} von 2.000 Zeichen“ ab 1.800 Zeichen, darunter null. */
export function zeichenHinweis(n: number): string | null {
  return n < ZAEHLER_AB ? null : zeichenZaehler(zahl(n));
}

export type FehlerSatz = { satz: string; neueAnfrage: boolean };
const text = (daten: Record<string, unknown> | null) => (typeof daten?.error === 'string' && daten.error.trim() ? daten.error : null);

/** Der Satz zu einer Fehlerantwort (BAU-HILFE 1.4, F21-4) und ob „Neue Anfrage“ dahinter steht. code null: kein Netz oder keine
 *  Antwort des Servers. Kein Satz nennt einen Betrag. produkt für Sätze mit {Produktname}; heute nennt ihn keiner. */
export function fehlerSatz(code: string | null, daten: Record<string, unknown> | null, produkt: string): FehlerSatz {
  switch (code) {
    case 'ASSISTANT_RATE_LIMIT': {
      if (daten?.art !== 'STUNDE') return { satz: CHAT_TEXTE.grenzeTag, neueAnfrage: true };
      const minuten = typeof daten.minuten === 'number' && Number.isInteger(daten.minuten) && daten.minuten > 0 ? daten.minuten : 60;
      return { satz: grenzeStunde(minuten), neueAnfrage: true };
    }
    case 'CONVERSATION_LIMIT': return { satz: CHAT_TEXTE.zuLang, neueAnfrage: true };
    case 'ASSISTANT_BUDGET': return { satz: CHAT_TEXTE.deckel, neueAnfrage: true };
    case 'ASSISTANT_OFF': return { satz: CHAT_TEXTE.aus, neueAnfrage: true };
    // Eingabe- und Zustandsfehler: der Text des Servers unter dem Feld.
    case 'VALIDATION':
    case 'CONVERSATION_NOT_FOUND':
    case 'OPERATOR_VIEW_READ_ONLY': {
      const satz = text(daten);
      return satz ? { satz, neueAnfrage: false } : { satz: CHAT_TEXTE.nichtErreichbar, neueAnfrage: true };
    }
    // 502 ASSISTANT_UNAVAILABLE, Netzfehler und alles Unbekannte.
    default: return { satz: CHAT_TEXTE.nichtErreichbar, neueAnfrage: true };
  }
}

export type TextBlock = { art: 'absatz' | 'liste'; zeilen: string[]; geordnet?: boolean };
const PUNKT = /^\s*[-–•]\s+/;
const NUMMER = /^\s*\d{1,3}[.)]\s+/;

/** Antworttext als Blöcke: Leerzeilen trennen Absätze, Zeilen mit „- “ oder „1. “ werden Aufzählungen (ohne das Zeichen).
 *  Alles bleibt Text; nichts wird als HTML oder Markdown gelesen. */
export function absaetze(eingabe: string): TextBlock[] {
  const bloecke: TextBlock[] = [];
  for (const teil of eingabe.replace(/\r\n?/g, '\n').split(/\n[ \t]*\n/)) {
    let aktuell: TextBlock | null = null;
    for (const zeile of teil.split('\n')) {
      if (!zeile.trim()) continue;
      const nummer = NUMMER.test(zeile);
      const art: TextBlock['art'] = nummer || PUNKT.test(zeile) ? 'liste' : 'absatz';
      const inhalt = art === 'liste' ? zeile.replace(nummer ? NUMMER : PUNKT, '').trim() : zeile.trim();
      if (!aktuell || aktuell.art !== art || (art === 'liste' && !!aktuell.geordnet !== nummer)) {
        aktuell = art === 'liste' && nummer ? { art, zeilen: [], geordnet: true } : { art, zeilen: [] };
        bloecke.push(aktuell);
      }
      aktuell.zeilen.push(inhalt);
    }
  }
  return bloecke;
}

/** Ziel einer genannten Anleitung: /hilfe/dokumente#hilfe-<id> (auch b-<uuid> eigener Dokumente). */
export const anleitungsLink = (id: string) => `${hilfeAdresse('dokumente')}#hilfe-${id}`;

/** Das Formular „Neue Anfrage“ mit dem Entwurf aus dieser Unterhaltung (PF-310-1). */
export const ticketAdresse = (unterhaltungId: string) => `${hilfeAdresse('tickets', 'neu')}?aus=chat&unterhaltung=${encodeURIComponent(unterhaltungId)}`;

const SEITE = /^\/[A-Za-z0-9/_-]*$/;
/** Die Seite für den Server: der Pfad ohne Abfrage und Anker, nur wenn er zum Muster des Servers passt und höchstens 300 Zeichen
 *  hat; sonst nichts. */
export function seiteFuerServer(pfad: string | null | undefined): string | undefined {
  const ohne = (pfad ?? '').split(/[?#]/)[0];
  return ohne.length <= 300 && SEITE.test(ohne) ? ohne : undefined;
}

const UHRZEIT = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
/** hh:mm nach Wiener Zeit; ohne gültige Zeit leer. */
export function uhrzeit(iso: string): string {
  const zeit = new Date(iso);
  return Number.isNaN(zeit.getTime()) ? '' : UHRZEIT.format(zeit);
}

/** Frage und Antwort nach dem Senden als zwei Nachrichten des Verlaufs; die Frage trägt die Zeit des Browsers. */
export function nachVersand(frage: string, antwort: AssistentAntwort, jetzt: Date): ChatNachricht[] {
  const zeit = jetzt.toISOString();
  const a = antwort.antwort;
  return [
    { id: `frage-${a.id}`, rolle: 'PERSON', text: frage, themen: [], ticketVorschlag: false, rechtsfrage: false, zeit },
    { id: a.id, rolle: 'ASSISTENT', text: a.text, themen: a.themen, ticketVorschlag: a.ticketVorschlag !== null, rechtsfrage: a.rechtsfrage, zeit },
  ];
}

/** aria-describedby aus den gesetzten Kennungen; ohne eine undefined. */
export const idListe = (...ids: (string | false)[]) => ids.filter(Boolean).join(' ') || undefined;

/** „Ticket anlegen“ ist aktiv ab der ersten Antwort. */
export const hatAntwort = (nachrichten: ChatNachricht[]) => nachrichten.some(nachricht => nachricht.rolle === 'ASSISTENT');
